import type { Department } from "../contexts/AuthProvider";
import type { DepartmentLocation, NurseInShift } from "./models";
import { getRoleGroupsForRole } from "./roles";

/** How many of `shifts` each of the department's role groups holds; a role in several groups counts in each. */
export const roleGroupCountsOf = (shifts: NurseInShift[], department: Department): Record<string, number> => {
	const roleGroupCounts: Record<string, number> = {};
	for (const shift of shifts) {
		for (const roleGroup of getRoleGroupsForRole(shift.role, department)) {
			roleGroupCounts[roleGroup] = (roleGroupCounts[roleGroup] ?? 0) + 1;
		}
	}
	return roleGroupCounts;
};

/** How many of `shifts` each job holds; a shift with no job isn't counted. */
export const jobGroupCountsOf = (shifts: NurseInShift[]): Record<string, number> => {
	const jobGroupCounts: Record<string, number> = {};
	for (const shift of shifts) {
		if (shift.jobId) jobGroupCounts[shift.jobId] = (jobGroupCounts[shift.jobId] ?? 0) + 1;
	}
	return jobGroupCounts;
};

/**
 * The department location a shift works at. Locations can share a name, so among those with the shift's location name
 * it picks, in order: the one matching its role (or job, with job management) and its hours, then its role or job, then
 * its hours, then the first. Undefined when the shift has no location or no location has its name.
 */
export const departmentLocationOfShift = (
	shift: NurseInShift,
	locations: DepartmentLocation[],
	jobManagementEnabled: boolean,
): DepartmentLocation | undefined => {
	if (!shift.location) return undefined;
	const locationsWithSameName = locations.filter((location) => location.location === shift.location);
	if (locationsWithSameName.length <= 1) return locationsWithSameName[0];

	const shiftStart = shift.startTime?.toFormat("HH:mm");
	const shiftEnd = shift.endTime?.toFormat("HH:mm");
	const matchesRoleOrJob = (location: DepartmentLocation): boolean => {
		if (jobManagementEnabled && location.jobId) return !!shift.jobId && location.jobId === shift.jobId;
		return location.roles.includes("all") || (!!shift.role && location.roles.includes(shift.role));
	};
	const matchesHours = (location: DepartmentLocation): boolean => shiftStart === location.shift_start && shiftEnd === location.shift_end;

	return (
		locationsWithSameName.find((location) => matchesRoleOrJob(location) && matchesHours(location)) ??
		locationsWithSameName.find(matchesRoleOrJob) ??
		locationsWithSameName.find(matchesHours) ??
		// DEFAULT: same-named locations that match neither the role nor the hours: the first, as the provider picked.
		locationsWithSameName[0]
	);
};
